import { IMaterial, Ipart, Iproduct } from '../models';
import { parseServerDate } from './serverDate';
import { partTypeEnum, productCommunicationMethodEnum } from '../models-enums';
import { v4 as uuidv4 } from 'uuid';

/**
 * Material DTO from the /material/stream endpoint (abbreviated field names)
 */
export interface MaterialStreamDto {
    id?: string; // Present if response is an array
    sku?: string;
    num?: string;
    name: string;
    desc?: string;
    sap?: boolean;
    type: number; // 0=Part, 1=Product
    date: string; // ISO date string
    del?: boolean;
    // Part-specific fields
    meas?: string;
    instTime?: number;
    partType?: number;
    // Product-specific fields
    pgc?: string;
    subCatId?: string;
    brandId?: string;
    prodTypeId?: string;
    powerId?: string;
    sysSizeId?: string;
    isFinal?: boolean;
    approved?: boolean;
    compliant?: boolean;
    hasSpares?: boolean;
    notes?: string;
    commMethod?: number;
    mergedId?: string;
    imgPath?: string;
    links?: Array<{
        id: string;
        url: string;
        type: number;
        other: string;
    }>;
    standards?: string[];
}

/**
 * Normalizes the Material Stream API response to a consistent array format.
 * Handles both dictionary (keyed by GUID) and array (with id field) formats.
 *
 * @param data - Response from /material/stream endpoint
 * @returns Array of materials with id property
 */
export function normalizeMaterialResponse(data: any): MaterialStreamDto[] {
    if (!data) {
        return [];
    }

    // If it's already an array, return as-is
    if (Array.isArray(data)) {
        return data as MaterialStreamDto[];
    }

    // If it's a dictionary keyed by GUID, convert to array
    if (typeof data === 'object') {
        return Object.entries(data).map(([id, material]) => ({
            id,
            ...(material as MaterialStreamDto)
        }));
    }

    return [];
}

/**
 * Converts MaterialStreamDto (abbreviated API format) to IMaterial (full property names).
 * 
 * @param dto - Material DTO from the stream endpoint
 * @param materialId - Optional material ID (if not present in the DTO)
 * @returns IMaterial with full property names
 */
export function streamDtoToMaterial(dto: MaterialStreamDto, materialId?: string): IMaterial {
    const material: IMaterial = {
        id: dto.id || materialId || '',
        sku: dto.sku || '',
        number: dto.num || '',
        name: dto.name,
        sap: dto.sap || false,
        type: dto.type,
        date: dto.date
    };

    // Only include optional properties if they have values
    if (dto.desc !== undefined) material.description = dto.desc;
    if (dto.del !== undefined) material.del = dto.del;
    
    // Part-specific
    if (dto.meas !== undefined) material.measurement = dto.meas;
    if (dto.instTime !== undefined) material.installationTime = dto.instTime;
    if (dto.partType !== undefined) material.partType = dto.partType;
    
    // Product-specific
    if (dto.pgc !== undefined) material.pgc = dto.pgc;
    if (dto.subCatId !== undefined) material.subcategoryID = dto.subCatId;
    if (dto.brandId !== undefined) material.brandID = dto.brandId;
    if (dto.prodTypeId !== undefined) material.productTypeID = dto.prodTypeId;
    if (dto.powerId !== undefined) material.powerID = dto.powerId;
    if (dto.sysSizeId !== undefined) material.systemSizeID = dto.sysSizeId;
    if (dto.isFinal !== undefined) material.isFinalProduct = dto.isFinal;
    if (dto.approved !== undefined) material.isApproved = dto.approved;
    if (dto.compliant !== undefined) material.isCompliant = dto.compliant;
    if (dto.hasSpares !== undefined) material.hasSpares = dto.hasSpares;
    if (dto.commMethod !== undefined) material.communicationMethod = dto.commMethod;
    if (dto.mergedId !== undefined) material.mergedMaterialID = dto.mergedId;
    if (dto.imgPath !== undefined) material.imagePath = dto.imgPath;
    if (dto.links !== undefined) material.links = dto.links;
    if (dto.standards !== undefined) material.standards = dto.standards;

    return material;
}

/**
 * Maps an IMaterial to an Iproduct object for Redux storage.
 * This retrofit layer allows the new Material API to work with the existing Product Redux structure.
 *
 * @param material - Material with full property names
 * @returns Iproduct object ready for Redux storage
 */
export function materialToProduct(material: IMaterial): Iproduct {
    const product: Iproduct = {
        id: material.id,
        name: material.name,
        sku: material.sku || '',
        description: material.description || '',
        imagePath: material.imagePath || '',
        subcategoryID: material.subcategoryID || '',
        brandID: material.brandID || '',
        productTypeID: material.productTypeID || '',
        powerID: material.powerID || '',
        systemSizeID: material.systemSizeID || '',
        isFinalProduct: material.isFinalProduct || false,
        // Server only sends approved:false when not approved, so default to true
        isApproved: material.isApproved !== undefined ? material.isApproved : true,
        // Server only sends compliant:false when not compliant, so default to true
        isCompliant: material.isCompliant !== undefined ? material.isCompliant : true,
        // Server only sends hasSpares:false when no spares, so default to true
        hasSpares: material.hasSpares !== undefined ? material.hasSpares : true,
        isDeleted: material.del ?? false,
        communicationMethod: (material.communicationMethod as productCommunicationMethodEnum) || productCommunicationMethodEnum.none,
        mergedProductID: material.mergedMaterialID || '',
        pgc: material.pgc || '',
        // Map date field to updateDate for delta tracking
        updateDate: material.date ? new Date(parseServerDate(material.date)) : undefined,
        createDate: undefined,
        // Map standards array to productStandards format
        productStandards: material.standards
            ? material.standards.map(standardID => ({
                  id: uuidv4(),
                  materialID: material.id,
                  standardID,
                  isDeleted: false
              }))
            : [],
        links: [],
        isActive: true
    };

    // Add optional properties only if they have values
    if (material.sap) {
        product.sapMaterialNumber = 'SAP';
    }

    return product;
}

/**
 * Reverse-maps an Iproduct back to IMaterial for unified IndexedDB storage.
 */
export function productToMaterial(product: Iproduct): IMaterial {
    const material: IMaterial = {
        id: product.id,
        sku: product.sku || '',
        number: product.sapMaterialNumber || '',
        name: product.name,
        sap: !!product.sapMaterialNumber,
        type: 1,
        date: product.updateDate ? new Date(product.updateDate).toISOString() : new Date().toISOString()
    };

    if (product.description) material.description = product.description;
    material.del = product.isDeleted;
    if (product.pgc) material.pgc = product.pgc;
    if (product.subcategoryID) material.subcategoryID = product.subcategoryID;
    if (product.brandID) material.brandID = product.brandID;
    if (product.productTypeID) material.productTypeID = product.productTypeID;
    if (product.powerID) material.powerID = product.powerID;
    if (product.systemSizeID) material.systemSizeID = product.systemSizeID;
    if (product.isFinalProduct !== undefined) material.isFinalProduct = product.isFinalProduct;
    if (product.isApproved !== undefined) material.isApproved = product.isApproved;
    if (product.isCompliant !== undefined) material.isCompliant = product.isCompliant;
    if (product.hasSpares !== undefined) material.hasSpares = product.hasSpares;
    if (product.communicationMethod !== undefined) material.communicationMethod = product.communicationMethod;
    if (product.mergedProductID) material.mergedMaterialID = product.mergedProductID;
    if (product.imagePath) material.imagePath = product.imagePath;
    if (product.productStandards?.length) {
        material.standards = product.productStandards.map(ps => ps.standardID);
    }

    return material;
}

/**
 * Maps an IMaterial to an Ipart object for Redux storage.
 * This retrofit layer allows the new Material API to work with the existing Part Redux structure.
 *
 * @param material - Material with full property names
 * @returns Ipart object ready for Redux storage
 */
export function materialToPart(material: IMaterial): Ipart {
    const part: Ipart = {
        id: material.id,
        number: material.number,
        description: material.description || material.name,
        installationTime: material.installationTime || 0,
        isActive: true, // Material is active if not deleted
        measurement: material.measurement || '',
        type: (material.partType as partTypeEnum) ?? partTypeEnum.default,
        isDeleted: material.del ?? false
    };

    // Only add optional date properties if they have values
    if (material.date) {
        part.updateDate = material.date;
    }

    return part;
}
